import React, { useRef, useState } from 'react';
import { ReactComponent as ClipIcon } from '@repo/common/assets/icons/16px/icon_16px_links.svg';
import { cn } from './model/derive';
import { extOf, formatSize, kindOf, toAttachment } from './fileUtils';
import { uploadBoardFile } from '../../api/twoDays/board.api';

export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_FILES = 10;

/**
 * 첨부 고르기 — 선택 또는 드래그 앤 드롭, 행별 삭제. 한 파일 20MB · 10개까지 (서버도 같은 기준).
 * Add task(리더) · Submit deliverable(담당자)에서 같이 쓴다.
 */
const FileDrop = ({ files, onChange, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const add = (list) => {
    const picked = Array.from(list ?? []);
    const tooBig = picked.filter((f) => f.size > MAX_FILE_BYTES);
    const ok = picked.filter((f) => f.size > 0 && f.size <= MAX_FILE_BYTES);
    const next = [...files, ...ok];
    setError(
      tooBig.length
        ? `${tooBig.map((f) => f.name).join(', ')} is over 20 MB.`
        : next.length > MAX_FILES
          ? `Up to ${MAX_FILES} files.`
          : '',
    );
    onChange(next.slice(0, MAX_FILES));
  };

  return (
    <div className='flex flex-col gap-2'>
      <div
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && fileRef.current?.click()}
        onKeyDown={(e) =>
          !disabled && (e.key === 'Enter' || e.key === ' ') && fileRef.current?.click()
        }
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) add(e.dataTransfer.files);
        }}
        className={cn(
          'flex h-16 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed text-[14px] text-text-secondary',
          dragging
            ? 'border-green-deep02 bg-surface-green10'
            : 'border-green-deep03/30 bg-functional-success01/40',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <ClipIcon className='h-4 w-4 text-icon-secondary' />
        <span>
          <span className='font-medium text-green-deep03'>Choose files</span> or drop them here
        </span>
        <input
          ref={fileRef}
          type='file'
          multiple
          className='hidden'
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <p role='alert' className='text-[12px] text-functional-failed04'>
          {error}
        </p>
      )}
      {files.length > 0 && (
        <ul className='flex flex-col gap-2'>
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className='flex items-center gap-3 rounded-lg border border-green-deep03/[0.12] px-3 py-2'
            >
              <span className='flex h-8 w-10 shrink-0 items-center justify-center rounded-md bg-green-deep03/[0.06] text-[11px] font-medium text-text-secondary'>
                {(extOf(f.name) || 'file').toUpperCase().slice(0, 4)}
              </span>
              <span className='flex min-w-0 flex-1 flex-col'>
                <span className='truncate text-[14px] text-text-primary'>{f.name}</span>
                <span className='text-[12px] text-text-secondary'>
                  {f.kind || kindOf(f.name)} ·{' '}
                  {typeof f.size === 'number' ? formatSize(f.size) : f.size}
                </span>
              </span>
              <button
                type='button'
                aria-label={`Remove ${f.name}`}
                disabled={disabled}
                className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[18px] text-text-secondary hover:bg-green-deep03/[0.06] hover:text-text-primary'
                onClick={() => onChange(files.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * 고른 파일 → 저장할 첨부. 실제 보드는 S3 에 올리고 키를, 목 보드는 브라우저 임시 주소를 쓴다.
 * @param taskId 있으면 담당자 결과물 첨부 (서버 권한이 다르다)
 * @param purpose 'comment' 면 댓글 사진 (팀원 누구나 · 이미지만)
 */
export const uploadAttachments = async ({ live, slug, taskId, purpose }, files) =>
  live && slug
    ? Promise.all(
        files.map(async (f) =>
          f.prev != null
            ? { prev: f.prev, name: f.name }
            : {
                name: f.name,
                kind: kindOf(f.name),
                size: f.size,
                key: await uploadBoardFile(slug, f, taskId, purpose),
              },
        ),
      )
    : files.map((f) => {
        if (f.prev == null) return toAttachment(f);
        const { prev, ...kept } = f;
        return kept;
      });

export default FileDrop;
